import { getCategoryBadgeClass, getCategoryLabel } from '@/lib/classColors'
import { HeroAwardCard } from '@/components/hero-award'
import { ClubLeaderboardCard } from '@/pages/awards/ClubLeaderboardCard'
import { parseApiErrorMessage } from '@/lib/api-errors'
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { DEFAULT_PLACE_COUNT, EntryCode, leaderboardPlaces } from '@schemas'
import type { LeaderboardOutput, ShowSpecialAwardsOutput } from '@schemas'
import {
  useDivisions,
  useLeaderboard,
  useLeaderboardCsv,
  useFinalizeAwards,
  useSpecialAwards,
  useSetBestInShow,
  useClearBestInShow,
  useSetBestInDivision,
  useClearBestInDivision,
  useSetChampionAwards,
} from '../api/hooks'
import { toast } from 'sonner'
import { useCurrentShow } from '@/context/current-show'
import { useRunMode, type UIMode } from '@/context/run-mode'
import { CurrentShowSummary, SelectShowNotice } from '@/components/current-show'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table'
import { Link } from 'react-router-dom'
import { PageHeading } from '@/components/page-heading'
import { getSpecialAwardLabel } from '@/data/special-awards'
import { Separator } from '@/components/ui/separator'
import { TrophyIcon } from 'lucide-react'
import { useQueryClient, type UseQueryResult } from '@tanstack/react-query'

const PLACEMENT_TIEBREAKER_ORDER = ['1st', '2nd', '3rd', '4th', '5th', '6th'] as const
const LEADERBOARD_PAGE_SIZE = 25
const EMPTY_LEADERBOARD: ReadonlyArray<LeaderboardOutput['leaderboard'][number]> = Object.freeze([])

function normalizeExhibitCode(value: string): string {
  return value.trim().toUpperCase()
}

function validateExhibitCode(value: string): string | null {
  if (!value) {
    return 'Enter an Exhibit ID.'
  }
  if (!EntryCode.safeParse(value).success) {
    return 'Exhibit ID must be four letters or digits and cannot be 0000.'
  }
  return null
}

type WinnerLike = { className?: string | null; entryTitle?: string | null } | null | undefined

function getWinnerDetailLabel(winner: WinnerLike): string | null {
  const className = winner?.className?.trim()
  if (className) return className
  const title = winner?.entryTitle?.trim()
  return title && title.length > 0 ? title : null
}

type DivisionSummary = {
  id: string
  name: string
  code: string | null
}

interface SpecialAwardsPanelProps {
  showId: string
  divisions: DivisionSummary[]
  reserveGrandEnabled: boolean
  championLeaders?: LeaderboardOutput['championLeaders']
  reserveEligibleLeaderboard?: LeaderboardOutput['reserveEligibleLeaderboard']
  leaderboard: ReadonlyArray<LeaderboardOutput['leaderboard'][number]>
  exhibitorNameById: Map<string, string>
  mode: UIMode
  isJudgingFinalized: boolean
}

function SpecialAwardsPanel({
  showId,
  divisions,
  reserveGrandEnabled: reserveGrandEnabledFallback,
  championLeaders,
  reserveEligibleLeaderboard,
  leaderboard,
  exhibitorNameById,
  mode,
  isJudgingFinalized,
}: SpecialAwardsPanelProps) {
  const specialAwards = useSpecialAwards(showId)
  const setBestInShow = useSetBestInShow()
  const clearBestInShow = useClearBestInShow()
  const setBestInDivision = useSetBestInDivision()
  const clearBestInDivision = useClearBestInDivision()
  const setChampionAwards = useSetChampionAwards()

  const [bestInShowInput, setBestInShowInput] = useState('')
  const [bestInShowError, setBestInShowError] = useState<string | null>(null)
  const [divisionInputs, setDivisionInputs] = useState<Record<string, string>>({})
  const [divisionErrors, setDivisionErrors] = useState<Record<string, string | null>>({})
  const [championSelection, setChampionSelection] = useState('')
  const [reserveSelection, setReserveSelection] = useState<string[]>([])
  const [championSelectionError, setChampionSelectionError] = useState<string | null>(null)
  const [reserveSelectionError, setReserveSelectionError] = useState<string | null>(null)
  const [championFormError, setChampionFormError] = useState<string | null>(null)
  const previousAwardInputs = useRef({
    showId,
    bestInShow: '',
    divisions: {} as Record<string, string>,
  })

  useEffect(() => {
    if (!specialAwards.data) return

    const previous = previousAwardInputs.current
    const changedShow = previous.showId !== showId
    const nextBest = specialAwards.data.bestInShow?.exhibitId ?? ''
    setBestInShowInput(draft => (changedShow || draft === previous.bestInShow ? nextBest : draft))

    const nextInputs: Record<string, string> = {}
    for (const division of divisions) {
      const winner = specialAwards.data.bestInDivision.find(
        entry => entry.divisionId === division.id
      )
      nextInputs[division.id] = winner?.winner?.exhibitId ?? ''
    }
    setDivisionInputs(drafts =>
      Object.fromEntries(
        Object.entries(nextInputs).map(([id, value]) => [
          id,
          changedShow || (drafts[id] ?? '') === (previous.divisions[id] ?? '') ? value : drafts[id],
        ])
      )
    )
    previousAwardInputs.current = {
      showId,
      bestInShow: nextBest,
      divisions: nextInputs,
    }
  }, [specialAwards.data, divisions, showId])

  const specialAwardsData = specialAwards.data
  const reserveGrandEnabled = specialAwardsData?.reserveGrandEnabled ?? reserveGrandEnabledFallback

  const leaderboardEntryMap = useMemo(() => {
    const map = new Map<string, LeaderboardOutput['leaderboard'][number]>()
    for (const entry of leaderboard) {
      map.set(entry.exhibitorId, entry)
    }
    return map
  }, [leaderboard])

  const recordedChampionAward = specialAwardsData?.grandChampion ?? null
  const reserveAwards = reserveGrandEnabled
    ? (specialAwardsData?.reserveGrands ??
      (specialAwardsData?.reserveGrand ? [specialAwardsData.reserveGrand] : []))
    : []
  const reserveAward = reserveAwards[0] ?? null
  const championLeaderEntry = championLeaders?.champion ?? null
  const reserveLeaderEntry = reserveGrandEnabled ? (championLeaders?.reserve ?? null) : null
  const tieGroups = championLeaders?.tieGroups ?? []

  const sortedTieGroups = useMemo(() => [...tieGroups].sort((a, b) => a.rank - b.rank), [tieGroups])

  const currentChampionTie = !championLeaderEntry
    ? sortedTieGroups.find(
        group => group.awardType === 'grand_champion' || group.awardType === undefined
      )
    : undefined
  const championAward =
    !championLeaders ||
    (championLeaderEntry
      ? recordedChampionAward?.exhibitorId === championLeaderEntry.exhibitorId
      : currentChampionTie?.exhibitorIds.includes(recordedChampionAward?.exhibitorId ?? ''))
      ? recordedChampionAward
      : null
  const championNeedsSelection =
    !championAward && !championLeaderEntry && Boolean(currentChampionTie)
  const championTieGroup = championNeedsSelection ? (currentChampionTie ?? null) : null
  const championCandidates = useMemo(
    () => (championTieGroup ? [...championTieGroup.exhibitorIds] : []),
    [championTieGroup]
  )
  const selectedChampionId =
    championSelection || championAward?.exhibitorId || championLeaderEntry?.exhibitorId
  const reserveCandidates = useMemo(() => {
    if (reserveEligibleLeaderboard) {
      const eligible = reserveEligibleLeaderboard.filter(
        entry => entry.exhibitorId !== selectedChampionId
      )
      const points = eligible[0]?.points
      return eligible.filter(entry => entry.points === points).map(entry => entry.exhibitorId)
    }
    return [...(sortedTieGroups[0]?.exhibitorIds ?? [])]
  }, [reserveEligibleLeaderboard, selectedChampionId, sortedTieGroups])
  const reserveCanSelect =
    reserveGrandEnabled &&
    (reserveEligibleLeaderboard
      ? reserveCandidates.length > 1 || (championNeedsSelection && reserveCandidates.length > 0)
      : !reserveAward &&
        ((championNeedsSelection && sortedTieGroups.length > 0) ||
          (!championNeedsSelection && !reserveLeaderEntry && sortedTieGroups.length > 0)))

  const savedReserveIds = reserveAwards
    .map(award => award.exhibitorId)
    .filter((id): id is string => Boolean(id))
  const reserveNeedsSelection =
    reserveCanSelect &&
    (!savedReserveIds.length || savedReserveIds.some(id => !reserveCandidates.includes(id)))
  const savedReserveKey = JSON.stringify(savedReserveIds.slice().sort())

  useEffect(() => {
    setReserveSelection(JSON.parse(savedReserveKey) as string[])
    setReserveSelectionError(null)
  }, [showId, savedReserveKey])

  useEffect(() => {
    if (!championNeedsSelection) {
      setChampionSelection('')
      setChampionSelectionError(null)
      return
    }
    if (championSelection && !championCandidates.includes(championSelection)) {
      setChampionSelection('')
    }
  }, [championNeedsSelection, championSelection, championCandidates])

  useEffect(() => {
    if (!reserveCanSelect) {
      setReserveSelection(current => (current.length ? [] : current))
      setReserveSelectionError(null)
      return
    }
    if (reserveSelection.some(id => !reserveCandidates.includes(id))) {
      setReserveSelection(current => current.filter(id => reserveCandidates.includes(id)))
    }
  }, [reserveCanSelect, reserveSelection, reserveCandidates])

  useEffect(() => {
    if (championSelection && championSelectionError) {
      setChampionSelectionError(null)
    }
  }, [championSelection, championSelectionError])

  useEffect(() => {
    if (reserveSelection.length > 0 && reserveSelectionError) {
      setReserveSelectionError(null)
    }
  }, [reserveSelection, reserveSelectionError])

  useEffect(() => {
    if (championFormError) {
      setChampionFormError(null)
    }
  }, [championSelection, reserveSelection, championNeedsSelection, reserveCanSelect])

  const resolveLeaderboardEntry = (
    exhibitorId: string | null | undefined,
    fallback?: LeaderboardOutput['leaderboard'][number] | null
  ) => {
    if (!exhibitorId) return fallback ?? null
    return leaderboardEntryMap.get(exhibitorId) ?? fallback ?? null
  }

  const formatLeaderboardDetail = (
    entry: LeaderboardOutput['leaderboard'][number] | null | undefined
  ) => {
    if (!entry) return null
    const entriesLabel = entry.entriesCount === 1 ? 'entry' : 'entries'
    return `${entry.points} pts • ${entry.entriesCount} ${entriesLabel}`
  }

  const getExhibitorName = (exhibitorId: string | null | undefined) => {
    if (!exhibitorId) return null
    return exhibitorNameById.get(exhibitorId) ?? 'Unknown exhibitor'
  }

  const formatCandidateLabel = (exhibitorId: string) => {
    const name = getExhibitorName(exhibitorId) ?? 'Unknown exhibitor'
    const detail = formatLeaderboardDetail(leaderboardEntryMap.get(exhibitorId))
    return detail ? `${name} — ${detail}` : name
  }

  const championExhibitorId = championAward?.exhibitorId ?? championLeaderEntry?.exhibitorId ?? null
  const championLeaderboardEntry = resolveLeaderboardEntry(championExhibitorId, championLeaderEntry)
  const championDisplayName =
    championAward?.entrantName ??
    championAward?.exhibitorName ??
    getExhibitorName(championExhibitorId)
  const championDetail = formatLeaderboardDetail(championLeaderboardEntry)
  const championHasDeterminate = Boolean(
    championAward || championLeaderEntry || championLeaderboardEntry
  )
  const championStatus: 'final' | 'tentative' | 'pending' = championHasDeterminate
    ? isJudgingFinalized && Boolean(championAward)
      ? 'final'
      : 'tentative'
    : 'pending'

  const reserveExhibitorId = reserveAward?.exhibitorId ?? reserveLeaderEntry?.exhibitorId ?? null
  const reserveLeaderboardEntry = reserveGrandEnabled
    ? resolveLeaderboardEntry(reserveExhibitorId, reserveLeaderEntry)
    : null
  const reserveDisplayName = reserveGrandEnabled
    ? reserveAwards.length
      ? reserveAwards
          .map(winner => winner.entrantName ?? winner.exhibitorName ?? 'Unknown exhibitor')
          .join(' and ')
      : getExhibitorName(reserveExhibitorId)
    : null
  const reserveDetail = reserveGrandEnabled
    ? reserveAwards.length > 1
      ? reserveAwards
          .map(
            winner =>
              `${winner.entrantName ?? winner.exhibitorName}: ${formatLeaderboardDetail(leaderboardEntryMap.get(winner.exhibitorId ?? '')) ?? ''}`
          )
          .join('; ')
      : formatLeaderboardDetail(reserveLeaderboardEntry)
    : null
  const reserveHasDeterminate = reserveGrandEnabled
    ? Boolean(reserveAward || reserveLeaderEntry || reserveLeaderboardEntry)
    : false
  const reserveStatus: 'final' | 'tentative' | 'pending' = reserveGrandEnabled
    ? reserveHasDeterminate
      ? isJudgingFinalized && Boolean(reserveAward)
        ? 'final'
        : 'tentative'
      : 'pending'
    : 'pending'

  const tieGroupToDescribe = championNeedsSelection
    ? championTieGroup
    : reserveCanSelect
      ? { exhibitorIds: reserveCandidates, rank: null }
      : null
  const tieDescriptionEntries = tieGroupToDescribe
    ? tieGroupToDescribe.exhibitorIds.map(id => ({
        id,
        label: formatCandidateLabel(id),
      }))
    : []
  const tieCalloutHeading = championNeedsSelection
    ? 'Grand Champion tie detected'
    : reserveNeedsSelection
      ? 'Reserve Grand Champion tie detected'
      : 'Reserve Grand Champion selection'
  const tieRankLabel =
    tieGroupToDescribe && tieGroupToDescribe.rank ? `Rank ${tieGroupToDescribe.rank}` : null

  const showTieBreakForm = mode === 'run' && (championNeedsSelection || reserveCanSelect)

  const championPendingMessage = championNeedsSelection
    ? 'Pending tie-breaker.'
    : 'No eligible entrant yet.'
  const reservePendingMessage = reserveCanSelect
    ? championNeedsSelection
      ? 'Select champion and reserve to break the tie.'
      : 'Pending tie-breaker.'
    : 'No eligible entrant yet.'

  const renderAwardSummary = ({
    label,
    name,
    detail,
    status,
    pendingMessage,
  }: {
    label: string
    name: string | null
    detail: string | null
    status: 'final' | 'tentative' | 'pending'
    pendingMessage: string
  }) => (
    <div className="rounded-md border border-border/60 bg-muted/40 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <p className="text-sm font-semibold text-foreground">{label}</p>
          {name ? (
            <>
              <p className="text-sm text-muted-foreground">{name}</p>
              {detail ? <p className="text-xs text-muted-foreground">{detail}</p> : null}
            </>
          ) : (
            <p className="text-sm text-muted-foreground">{pendingMessage}</p>
          )}
        </div>
        {status === 'tentative' ? <Badge variant="secondary">Tentative</Badge> : null}
      </div>
    </div>
  )

  const bestInDivisionEnabled = specialAwardsData?.bestInDivisionEnabled ?? false
  const isMutatingBestInShow = setBestInShow.isPending || clearBestInShow.isPending
  const isMutatingDivision = setBestInDivision.isPending || clearBestInDivision.isPending
  const isLoadingSpecialAwards = specialAwards.isLoading && !specialAwardsData
  const hasSpecialAwardsError = specialAwards.isError && !specialAwardsData

  const handleSetBestInShow = async () => {
    const normalized = normalizeExhibitCode(bestInShowInput)
    const error = validateExhibitCode(normalized)
    if (error) {
      setBestInShowError(error)
      return
    }
    try {
      await setBestInShow.mutateAsync({ showId, exhibitCode: normalized })
      setBestInShowError(null)
      toast.success('Best in Show updated')
    } catch (error) {
      const message = parseApiErrorMessage(error)
      setBestInShowError(message)
      toast.error(message)
    }
  }

  const handleClearBestInShow = async () => {
    try {
      await clearBestInShow.mutateAsync({ showId })
      setBestInShowInput('')
      setBestInShowError(null)
      toast.success('Best in Show cleared')
    } catch (error) {
      const message = parseApiErrorMessage(error)
      toast.error(message)
    }
  }

  const handleSetBestInDivision = async (divisionId: string, divisionName: string) => {
    const normalized = normalizeExhibitCode(divisionInputs[divisionId] ?? '')
    const error = validateExhibitCode(normalized)
    if (error) {
      setDivisionErrors(prev => ({ ...prev, [divisionId]: error }))
      return
    }
    try {
      await setBestInDivision.mutateAsync({
        showId,
        divisionId,
        exhibitCode: normalized,
      })
      setDivisionErrors(prev => ({ ...prev, [divisionId]: null }))
      toast.success(`Best in Division updated for ${divisionName}`)
    } catch (error) {
      const message = parseApiErrorMessage(error)
      setDivisionErrors(prev => ({ ...prev, [divisionId]: message }))
      toast.error(message)
    }
  }

  const handleClearBestInDivision = async (divisionId: string, divisionName: string) => {
    try {
      await clearBestInDivision.mutateAsync({ showId, divisionId })
      setDivisionInputs(previous => ({ ...previous, [divisionId]: '' }))
      setDivisionErrors(prev => ({ ...prev, [divisionId]: null }))
      toast.success(`Best in Division cleared for ${divisionName}`)
    } catch (error) {
      const message = parseApiErrorMessage(error)
      setDivisionErrors(prev => ({ ...prev, [divisionId]: message }))
      toast.error(message)
    }
  }

  const handleChampionTieSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    let hasError = false
    setChampionFormError(null)

    if (championNeedsSelection && !championSelection) {
      setChampionSelectionError('Select a Grand Champion entrant.')
      hasError = true
    }

    if (reserveGrandEnabled && reserveCanSelect) {
      if (reserveSelection.length === 0) {
        setReserveSelectionError('Select a Reserve Grand Champion entrant.')
        hasError = true
      } else if (
        (championNeedsSelection && reserveSelection.includes(championSelection)) ||
        (!championNeedsSelection && reserveSelection.includes(championExhibitorId ?? ''))
      ) {
        setReserveSelectionError('Champion and Reserve must be different entrants.')
        hasError = true
      }
    }

    if (hasError) return

    try {
      await setChampionAwards.mutateAsync({
        showId,
        championEntrantId: championNeedsSelection ? championSelection : undefined,
        reserveEntrantIds: reserveGrandEnabled && reserveCanSelect ? reserveSelection : undefined,
      })
      toast.success('Champion awards updated')
      setChampionSelection('')
      setChampionSelectionError(null)
      setReserveSelectionError(null)
      setChampionFormError(null)
    } catch (error) {
      const message = parseApiErrorMessage(error)
      setChampionFormError(message)
      toast.error(message)
    }
  }

  const divisionWinnerMap = useMemo(() => {
    const map = new Map<string, ShowSpecialAwardsOutput['bestInDivision'][number]>()
    specialAwardsData?.bestInDivision?.forEach(entry => {
      map.set(entry.divisionId, entry)
    })
    return map
  }, [specialAwardsData?.bestInDivision])

  const bestInShowDetail = getWinnerDetailLabel(specialAwardsData?.bestInShow)

  return (
    <Card>
      <CardHeader>
        <CardTitle>Special awards</CardTitle>
        <CardDescription>
          Review champion standings, assign Best in Show, and manage per-division winners.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoadingSpecialAwards ? (
          <p className="text-sm text-muted-foreground">Loading special awards…</p>
        ) : hasSpecialAwardsError ? (
          <p className="text-sm text-destructive">{parseApiErrorMessage(specialAwards.error)}</p>
        ) : (
          <>
            <section className="space-y-4">
              <div className="space-y-1">
                <h3 className="text-sm font-semibold text-foreground">Champion awards</h3>
                <p className="text-sm text-muted-foreground">
                  Grand Champion updates automatically from leaderboard points. Results remain
                  tentative until judging is finalized.
                </p>
              </div>
              <div className={reserveGrandEnabled ? 'grid gap-3 md:grid-cols-2' : 'space-y-3'}>
                {renderAwardSummary({
                  label: 'Grand Champion',
                  name: championDisplayName,
                  detail: championDetail,
                  status: championStatus,
                  pendingMessage: championPendingMessage,
                })}
                {reserveGrandEnabled
                  ? renderAwardSummary({
                      label: 'Reserve Grand Champion',
                      name: reserveDisplayName,
                      detail: reserveDetail,
                      status: reserveStatus,
                      pendingMessage: reservePendingMessage,
                    })
                  : null}
              </div>
              {(championNeedsSelection || reserveCanSelect) && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-4 space-y-3">
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-amber-900">{tieCalloutHeading}</p>
                    {tieRankLabel ? (
                      <p className="text-xs font-medium uppercase tracking-wide text-amber-800">
                        {tieRankLabel}
                      </p>
                    ) : null}
                    <p className="text-sm text-amber-900">
                      {championNeedsSelection || reserveNeedsSelection
                        ? `Choose the entrant${reserveGrandEnabled ? 's' : ''} to break the tie.`
                        : 'Saved recipients are selected below. You can update the shared award.'}
                    </p>
                  </div>
                  {tieDescriptionEntries.length > 0 ? (
                    <ul className="list-disc space-y-1 pl-5 text-sm text-amber-900">
                      {tieDescriptionEntries.map(candidate => (
                        <li key={candidate.id}>{candidate.label}</li>
                      ))}
                    </ul>
                  ) : null}
                  {showTieBreakForm ? (
                    <form onSubmit={handleChampionTieSubmit} className="space-y-3">
                      <div className="grid gap-3 md:grid-cols-2">
                        {championNeedsSelection ? (
                          <div className="space-y-1">
                            <Label
                              htmlFor="champion-select"
                              className="text-sm font-medium text-amber-900"
                            >
                              Grand Champion
                            </Label>
                            <Select
                              value={championSelection || undefined}
                              onValueChange={value => setChampionSelection(value)}
                              disabled={setChampionAwards.isPending}
                            >
                              <SelectTrigger id="champion-select">
                                <SelectValue placeholder="Select entrant" />
                              </SelectTrigger>
                              <SelectContent>
                                {championCandidates.map(candidateId => (
                                  <SelectItem key={candidateId} value={candidateId}>
                                    {formatCandidateLabel(candidateId)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {championSelectionError ? (
                              <p className="text-xs text-destructive">{championSelectionError}</p>
                            ) : null}
                          </div>
                        ) : null}
                        {reserveGrandEnabled && reserveCanSelect ? (
                          <div className="space-y-1">
                            <Label
                              htmlFor="reserve-select"
                              className="text-sm font-medium text-amber-900"
                            >
                              Reserve Grand Champion
                            </Label>
                            <fieldset
                              id="reserve-select"
                              className="space-y-2"
                              disabled={setChampionAwards.isPending}
                            >
                              <legend className="text-xs">Select one or more tied entrants</legend>
                              {reserveCandidates.map(candidateId => (
                                <label
                                  key={candidateId}
                                  className="flex items-center gap-2 text-sm"
                                >
                                  <input
                                    type="checkbox"
                                    checked={reserveSelection.includes(candidateId)}
                                    disabled={
                                      candidateId ===
                                      (championNeedsSelection
                                        ? championSelection
                                        : championExhibitorId)
                                    }
                                    onChange={event =>
                                      setReserveSelection(current =>
                                        event.target.checked
                                          ? [...current, candidateId]
                                          : current.filter(id => id !== candidateId)
                                      )
                                    }
                                  />
                                  {formatCandidateLabel(candidateId)}
                                </label>
                              ))}
                            </fieldset>
                            {reserveSelectionError ? (
                              <p className="text-xs text-destructive">{reserveSelectionError}</p>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                      {championFormError ? (
                        <p className="text-sm text-destructive">{championFormError}</p>
                      ) : null}
                      <Button type="submit" disabled={setChampionAwards.isPending}>
                        {setChampionAwards.isPending ? 'Saving…' : 'Save selection'}
                      </Button>
                    </form>
                  ) : (
                    <p className="text-sm text-amber-900">
                      Switch to Run mode to update champion selections.
                    </p>
                  )}
                </div>
              )}
            </section>

            <Separator />

            <section className="space-y-3">
              <div>
                <h3 className="text-sm font-semibold text-foreground">Best in Show</h3>
                <p className="text-sm text-muted-foreground">
                  {specialAwardsData?.bestInShow
                    ? `${specialAwardsData.bestInShow.exhibitId} — ${specialAwardsData.bestInShow.exhibitorName ?? 'Unknown exhibitor'}${bestInShowDetail ? ` (${bestInShowDetail})` : ''}`
                    : 'No winner recorded yet.'}
                </p>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-4">
                <div className="sm:w-64">
                  <label
                    htmlFor="best-in-show-input"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    Exhibit ID
                  </label>
                  <Input
                    id="best-in-show-input"
                    value={bestInShowInput}
                    onChange={event => {
                      const filtered = event.target.value
                        .toUpperCase()
                        .replace(/[^0-9A-Z]/g, '')
                        .slice(0, 4)
                      setBestInShowInput(filtered)
                      if (bestInShowError) setBestInShowError(null)
                    }}
                    inputMode="text"
                    autoComplete="off"
                    spellCheck={false}
                    maxLength={4}
                    disabled={isMutatingBestInShow}
                  />
                  {bestInShowError ? (
                    <p className="mt-1 text-xs text-destructive">{bestInShowError}</p>
                  ) : null}
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    onClick={handleSetBestInShow}
                    disabled={isMutatingBestInShow}
                  >
                    {setBestInShow.isPending ? 'Saving…' : 'Set winner'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClearBestInShow}
                    disabled={isMutatingBestInShow}
                  >
                    {clearBestInShow.isPending ? 'Clearing…' : 'Clear'}
                  </Button>
                </div>
              </div>
            </section>

            <Separator />

            <section className="space-y-3">
              <div>
                <h3 className="text-sm font-semibold text-foreground">Best in Division</h3>
                {bestInDivisionEnabled ? (
                  <p className="text-sm text-muted-foreground">
                    Record a single Exhibit ID for each division. Use the Clear action to remove a
                    recorded winner.
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Best in Division awards are disabled for this show. Enable them from the show
                    settings to record division champions.
                  </p>
                )}
              </div>

              {bestInDivisionEnabled && divisions.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-48">Division</TableHead>
                        <TableHead>Current winner</TableHead>
                        <TableHead className="w-40">Exhibit ID</TableHead>
                        <TableHead className="w-40">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {divisions.map(division => {
                        const winner = divisionWinnerMap.get(division.id)
                        const inputValue = divisionInputs[division.id] ?? ''
                        const error = divisionErrors[division.id] ?? null
                        const label = division.code
                          ? `${division.code} — ${division.name}`
                          : division.name
                        const winnerDetail = getWinnerDetailLabel(winner?.winner ?? null)
                        return (
                          <TableRow key={division.id}>
                            <TableCell className="align-top">
                              <div>
                                <p className="font-medium text-sm text-foreground">
                                  {division.name}
                                </p>
                                {division.code ? (
                                  <Badge
                                    variant="outline"
                                    className={getCategoryBadgeClass(division.code)}
                                  >
                                    {getCategoryLabel(division.code)}
                                  </Badge>
                                ) : null}
                              </div>
                            </TableCell>
                            <TableCell className="align-top text-sm text-muted-foreground">
                              {winner?.winner
                                ? `${winner.winner.exhibitId} — ${winner.winner.exhibitorName ?? 'Unknown exhibitor'}${winnerDetail ? ` (${winnerDetail})` : ''}`
                                : 'No winner recorded.'}
                            </TableCell>
                            <TableCell className="align-top">
                              <label htmlFor={`division-${division.id}-input`} className="sr-only">
                                Exhibit ID
                              </label>
                              <Input
                                id={`division-${division.id}-input`}
                                value={inputValue}
                                onChange={event => {
                                  const filtered = event.target.value
                                    .toUpperCase()
                                    .replace(/[^0-9A-Z]/g, '')
                                    .slice(0, 4)
                                  setDivisionInputs(prev => ({ ...prev, [division.id]: filtered }))
                                  if (error) {
                                    setDivisionErrors(prev => ({ ...prev, [division.id]: null }))
                                  }
                                }}
                                inputMode="text"
                                autoComplete="off"
                                spellCheck={false}
                                maxLength={4}
                                disabled={isMutatingDivision}
                              />
                              {error ? (
                                <p className="mt-1 text-xs text-destructive">{error}</p>
                              ) : null}
                            </TableCell>
                            <TableCell className="align-top">
                              <div className="flex gap-2">
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={() => handleSetBestInDivision(division.id, label)}
                                  disabled={isMutatingDivision}
                                >
                                  {setBestInDivision.isPending ? 'Saving…' : 'Set winner'}
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleClearBestInDivision(division.id, label)}
                                  disabled={isMutatingDivision}
                                >
                                  {clearBestInDivision.isPending ? 'Clearing…' : 'Clear'}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : bestInDivisionEnabled ? (
                <p className="text-sm text-muted-foreground">
                  Add divisions to the show to record per-division winners.
                </p>
              ) : null}
            </section>
          </>
        )}
      </CardContent>
    </Card>
  )
}

// Returns a callback ref for an element that a refresh may remove. React runs the cleanup before
// removing the node, so focus inside it can be moved to `replacement()` after the commit
// instead of being dropped on <body>.
function keepFocusOnRemoval<T extends HTMLElement>(
  ref: { current: T | null } | null,
  replacement: () => HTMLElement | null
) {
  return (node: T) => {
    if (ref) ref.current = node
    return () => {
      if (ref) ref.current = null
      if (!node.contains(document.activeElement)) return
      queueMicrotask(() => {
        // Leave focus alone if something else took it during the same update.
        const active = document.activeElement
        if (active && active !== document.body) return
        replacement()?.focus()
      })
    }
  }
}

interface LeaderboardCardProps {
  showName: string
  /** Places the show awards in each class. */
  defaultPlaceCount: number
  leaderboardQuery: UseQueryResult<LeaderboardOutput, Error>
  exhibitorNameById: Map<string, string>
  isExporting: boolean
  onExport: () => void
}

function LeaderboardCard({
  showName,
  defaultPlaceCount,
  leaderboardQuery,
  exhibitorNameById,
  isExporting,
  onExport,
}: LeaderboardCardProps) {
  const leaderboard: ReadonlyArray<LeaderboardOutput['leaderboard'][number]> =
    leaderboardQuery.data?.leaderboard ?? EMPTY_LEADERBOARD
  const [page, setPage] = useState(1)
  const tableRegionRef = useRef<HTMLDivElement | null>(null)
  const messageRef = useRef<HTMLParagraphElement | null>(null)
  const focusRefs = useMemo(() => {
    const replacement = () => tableRegionRef.current ?? messageRef.current
    return {
      tableRegion: keepFocusOnRemoval(tableRegionRef, replacement),
      message: keepFocusOnRemoval(messageRef, replacement),
      pager: keepFocusOnRemoval(null, replacement),
    }
  }, [])
  const filteredLeaderboard = useMemo(
    () => leaderboard.filter(entry => entry.points !== 0),
    [leaderboard]
  )
  const hiddenZeroCount = leaderboard.length - filteredLeaderboard.length
  const totalPages = Math.max(1, Math.ceil(filteredLeaderboard.length / LEADERBOARD_PAGE_SIZE))
  // When loaded results shrink past the current page, stay on the last page the user can see
  // rather than jumping forward again if rows return. Loading states keep the page.
  if (leaderboardQuery.data && page > totalPages) {
    setPage(totalPages)
  }
  const pageStart = (page - 1) * LEADERBOARD_PAGE_SIZE
  const displayedLeaderboard = filteredLeaderboard.slice(
    pageStart,
    pageStart + LEADERBOARD_PAGE_SIZE
  )

  const placementColumns = useMemo(
    () =>
      leaderboardPlaces(
        leaderboard.map(entry => entry.placements ?? {}),
        defaultPlaceCount
      ),
    [leaderboard, defaultPlaceCount]
  )

  const tieExplanations = useMemo(() => {
    const map = new Map<string, string>()
    if (leaderboard.length === 0) {
      return map
    }

    const getPlacementCount = (entry: (typeof leaderboard)[number], place: string): number => {
      return entry.placements?.[place] ?? 0
    }

    for (let index = 0; index < leaderboard.length; index += 1) {
      const entry = leaderboard[index]
      const previous = index > 0 ? leaderboard[index - 1] : undefined
      const next = index < leaderboard.length - 1 ? leaderboard[index + 1] : undefined

      const tiedWithPrevious =
        previous && previous.points === entry.points && previous.rank !== entry.rank
      const tiedWithNext = next && next.points === entry.points && next.rank !== entry.rank

      const comparison = tiedWithPrevious ? previous : tiedWithNext ? next : undefined
      if (!comparison) continue

      const placementKey = PLACEMENT_TIEBREAKER_ORDER.find(place => {
        return getPlacementCount(entry, place) !== getPlacementCount(comparison, place)
      })

      if (!placementKey) continue

      const entryRank = entry.rank
      const comparisonRank = comparison.rank
      if (entryRank === comparisonRank) continue

      const entryCount = getPlacementCount(entry, placementKey)
      const comparisonCount = getPlacementCount(comparison, placementKey)
      const moreOrFewer = entryCount > comparisonCount ? 'more' : 'fewer'
      const direction = entryRank < comparisonRank ? 'higher' : 'lower'
      const label = placementKey.endsWith('s') ? placementKey : `${placementKey}s`
      map.set(
        entry.exhibitorId,
        `Tie on points; ranked ${direction} due to ${moreOrFewer} ${label}`
      )
    }

    return map
  }, [leaderboard])

  const message = leaderboardQuery.isLoading
    ? 'Loading leaderboard…'
    : leaderboardQuery.isError
      ? (leaderboardQuery.error?.message ?? 'Failed to load leaderboard.')
      : leaderboard.length === 0
        ? 'No leaderboard data yet. Finalize awards to see exhibitor standings.'
        : filteredLeaderboard.length === 0
          ? 'No entrants with non-zero points to display.'
          : null
  const canExport = !isExporting && leaderboard.length > 0 && !leaderboardQuery.isError

  const goToPage = (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalPages) return
    // Render the new page before moving focus so the table region announces it.
    flushSync(() => setPage(nextPage))
    const region = tableRegionRef.current
    if (!region) return
    region.focus({ preventScroll: true })
    const { top } = region.getBoundingClientRect()
    if (top < 0 || top >= window.innerHeight) {
      region.scrollIntoView?.({ block: 'start' })
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center space-x-2">
          <TrophyIcon className="h-5 w-5 text-amber-500" />
          <CardTitle>Leaderboard</CardTitle>
        </div>
        <CardDescription>
          Exhibitor standings based on placement points for{' '}
          <span className="font-semibold text-primary">{showName}</span>.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {message !== null ? (
          <p
            ref={focusRefs.message}
            tabIndex={-1}
            className={`rounded-md text-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 ${
              leaderboardQuery.isError ? 'text-destructive' : 'text-muted-foreground'
            }`}
          >
            {message}
          </p>
        ) : (
          <>
            <div
              ref={focusRefs.tableRegion}
              role="region"
              aria-label={
                totalPages > 1 ? `Leaderboard, page ${page} of ${totalPages}` : 'Leaderboard'
              }
              tabIndex={-1}
              className="scroll-mt-4 overflow-x-auto rounded-md outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Rank</TableHead>
                    <TableHead>Exhibitor</TableHead>
                    <TableHead className="text-right">Points</TableHead>
                    <TableHead className="text-right">Entries</TableHead>
                    {placementColumns.map(column => (
                      <TableHead key={column} className="text-right">
                        {column}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayedLeaderboard.map(entry => {
                    const name = exhibitorNameById.get(entry.exhibitorId) ?? 'Unknown exhibitor'
                    const tieMessage = tieExplanations.get(entry.exhibitorId)
                    return (
                      <TableRow key={entry.exhibitorId}>
                        <TableCell className="font-semibold">{entry.rank}</TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium">{name}</span>
                            {tieMessage ? (
                              <span className="text-xs text-muted-foreground">{tieMessage}</span>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-semibold">{entry.points}</TableCell>
                        <TableCell className="text-right">{entry.entriesCount}</TableCell>
                        {placementColumns.map(column => (
                          <TableCell key={column} className="text-right">
                            {entry.placements[column] ?? 0}
                          </TableCell>
                        ))}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
            {hiddenZeroCount > 0 && (
              <p className="pt-4 text-sm text-muted-foreground">
                {hiddenZeroCount === 1
                  ? '1 entrant with zero points is not shown.'
                  : `${hiddenZeroCount} entrants with zero points are not shown.`}{' '}
                Ranks still count them, and Export CSV includes them.
              </p>
            )}
            {filteredLeaderboard.length > LEADERBOARD_PAGE_SIZE && (
              <nav
                ref={focusRefs.pager}
                aria-label="Leaderboard pagination"
                className="flex flex-wrap items-center justify-between gap-3 pt-4"
              >
                <div className="text-sm text-muted-foreground">
                  <p>
                    Showing {pageStart + 1}–{pageStart + displayedLeaderboard.length} of{' '}
                    {filteredLeaderboard.length} entrants
                  </p>
                  <p>
                    Page {page} of {totalPages}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    aria-disabled={page === 1}
                    onClick={() => goToPage(page - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    aria-disabled={page === totalPages}
                    onClick={() => goToPage(page + 1)}
                  >
                    Next
                  </Button>
                </div>
              </nav>
            )}
          </>
        )}
      </CardContent>
      <CardFooter className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <p className="text-sm text-muted-foreground">
          Ranks reflect the show's placement points and tie-breaker settings.
        </p>
        <Button
          variant="outline"
          className="aria-disabled:text-foreground"
          // aria-disabled keeps the button focusable while an export runs or data is unavailable.
          aria-disabled={!canExport}
          onClick={() => {
            if (canExport) onExport()
          }}
        >
          {isExporting ? 'Exporting…' : 'Export CSV'}
        </Button>
      </CardFooter>
    </Card>
  )
}

export default function AwardsPage() {
  const { currentShow, setCurrentShow } = useCurrentShow()
  const { mode } = useRunMode()
  const divisionsQuery = useDivisions(currentShow?.id)
  const leaderboardQuery = useLeaderboard(currentShow?.id)

  const incompleteClasses = useMemo(() => {
    if (!divisionsQuery.data)
      return [] as Array<{ id: string; code: string; name: string; divisionName: string }>
    return divisionsQuery.data.flatMap(division =>
      division.classes
        .filter(cls => !cls.noAwards && !cls.resultsFinalizedAt)
        .map(cls => ({
          id: cls.id ?? `${division.id}-${cls.code}`,
          code: cls.code,
          name: cls.name,
          divisionName: division.name,
        }))
    )
  }, [divisionsQuery.data])

  const divisionSummaries = useMemo<DivisionSummary[]>(() => {
    return (divisionsQuery.data ?? [])
      .filter(division => typeof division.id === 'string')
      .map(division => ({
        id: division.id as string,
        name: division.name ?? 'Untitled division',
        code: division.code ?? null,
      }))
  }, [divisionsQuery.data])

  // The leaderboard carries entrant names, so judges see them without the entrant list.
  const exhibitorNames = leaderboardQuery.data?.exhibitorNames
  const exhibitorNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const [id, name] of Object.entries(exhibitorNames ?? {})) {
      map.set(id, name.trim().length > 0 ? name : 'Unnamed exhibitor')
    }
    return map
  }, [exhibitorNames])

  const staffRestrictedAwards = useMemo(() => {
    const specials = leaderboardQuery.data?.specialAwards ?? []
    return specials
      .filter(award => award && !award.staffEligible && award.skippedStaffExhibitorIds.length > 0)
      .map(award => ({
        type: award.type,
        label: getSpecialAwardLabel(award.type),
        skippedNames: award.skippedStaffExhibitorIds.map(
          id => exhibitorNameById.get(id) ?? 'Unknown exhibitor'
        ),
      }))
  }, [leaderboardQuery.data?.specialAwards, exhibitorNameById])

  const leaderboardCsv = useLeaderboardCsv()
  const finalizeAwards = useFinalizeAwards()
  const queryClient = useQueryClient()
  const leaderboardData = leaderboardQuery.data
  const leaderboard = leaderboardData?.leaderboard ?? EMPTY_LEADERBOARD

  const handleDownloadCsv = (content: string, filename: string) => {
    const blob = new Blob([content], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    toast.success(`${filename} downloaded`)
  }

  const handleExportLeaderboard = async () => {
    if (!currentShow?.id) {
      toast.error('Select a show before exporting the leaderboard.')
      return
    }
    try {
      const csv = await leaderboardCsv.mutateAsync({ showId: currentShow.id })
      handleDownloadCsv(csv, 'leaderboard.csv')
    } catch (_error) {
      toast.error('Failed to export leaderboard CSV')
    }
  }

  const handleFinalizeAwards = async () => {
    if (!currentShow?.id) {
      toast.error('Select a show before finalizing awards.')
      return
    }
    try {
      const result = await finalizeAwards.mutateAsync({ showId: currentShow.id, awards: [] })
      setCurrentShow(result.show)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['leaderboard', currentShow.id] }),
        queryClient.invalidateQueries({ queryKey: ['special-awards', currentShow.id] }),
        queryClient.invalidateQueries({ queryKey: ['show', 'stats', currentShow.id] }),
      ])
      toast.success('Awards finalized')
    } catch (error) {
      toast.error(parseApiErrorMessage(error))
    }
  }

  if (mode === 'run' && !currentShow) {
    return (
      <SelectShowNotice
        title="Choose a show to finalize awards"
        description="Awards can only be finalized when a show is selected in Run mode."
        actionLabel="Select show"
      />
    )
  }

  if (mode === 'run' && currentShow && currentShow.status === 'draft') {
    return (
      <Card className="border-dashed border-amber-300 bg-amber-50">
        <CardHeader>
          <CardTitle>Activate the show before finalizing awards</CardTitle>
          <CardDescription>
            Move the show into the <code>Judging</code> phase before locking awards. Complete the
            setup checklist first.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button asChild>
            <Link to={`/${currentShow.slug}/setup`}>Open setup guide</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/shows">Manage shows</Link>
          </Button>
        </CardContent>
      </Card>
    )
  }

  if (!currentShow || !currentShow.id) {
    return <SelectShowNotice description="Choose a show to finalize awards and lock results." />
  }

  const showId = currentShow.id

  const isJudgingPhase = currentShow.status === 'judging'
  const canFinalizeAwards = isJudgingPhase && incompleteClasses.length === 0
  const finalizeButtonDisabled = !canFinalizeAwards || finalizeAwards.isPending

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <PageHeading>Awards</PageHeading>
        <p className="text-muted-foreground">
          Review outstanding classes, finalize placements, and lock results for the selected show.
        </p>
      </header>

      <SpecialAwardsPanel
        showId={showId}
        divisions={divisionSummaries}
        reserveGrandEnabled={currentShow.reserveGrandEnabled ?? false}
        championLeaders={leaderboardQuery.data?.championLeaders}
        reserveEligibleLeaderboard={leaderboardQuery.data?.reserveEligibleLeaderboard}
        leaderboard={leaderboard}
        exhibitorNameById={exhibitorNameById}
        mode={mode}
        isJudgingFinalized={
          Boolean(currentShow.judgingFinalizedAt) || currentShow.status === 'finalized'
        }
      />

      <HeroAwardCard show={currentShow} selection />

      {mode !== 'run' && <CurrentShowSummary show={currentShow} />}

      {staffRestrictedAwards.length > 0 && (
        <div className="space-y-2">
          {staffRestrictedAwards.map(award => {
            return (
              <div
                key={award.type}
                className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
              >
                <span className="font-medium">{award.label}</span> excludes show staff. Skipped
                entries:
                <span className="ml-1">{award.skippedNames.join(', ')}</span>
              </div>
            )
          })}
        </div>
      )}
      <Card>
        <CardHeader>
          <CardTitle>Finalize Awards</CardTitle>
          <CardDescription>
            Ensure every class is finalized and special awards are assigned. Finalization no longer
            requires entering a JSON payload.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {incompleteClasses.length > 0 ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
              <p className="font-medium">Finalize pending classes before locking the show.</p>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                {incompleteClasses.map(cls => (
                  <li key={cls.id}>
                    {cls.code} — {cls.name} ({cls.divisionName})
                  </li>
                ))}
              </ul>
            </div>
          ) : isJudgingPhase ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                All classes are finalized. Coordinate with reporting or automation to publish final
                awards when ready.
              </p>
              <Button
                type="button"
                onClick={handleFinalizeAwards}
                disabled={finalizeButtonDisabled}
              >
                {finalizeAwards.isPending ? 'Finalizing…' : 'Finalize awards'}
              </Button>
              {!canFinalizeAwards ? (
                <p className="text-xs text-muted-foreground">
                  Awards can only be finalized while the show is in the Judging phase.
                </p>
              ) : null}
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                All classes are finalized and this show is no longer in the Judging phase.
              </p>
              <p className="text-xs text-muted-foreground">
                Finalization is locked once a show is marked Finalized. Reopen judging from the Run
                dashboard if additional changes are required.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <LeaderboardCard
        key={showId}
        showName={currentShow.name}
        defaultPlaceCount={currentShow.defaultPlaceCount ?? DEFAULT_PLACE_COUNT}
        leaderboardQuery={leaderboardQuery}
        exhibitorNameById={exhibitorNameById}
        isExporting={leaderboardCsv.isPending}
        onExport={handleExportLeaderboard}
      />

      {currentShow.enableClubs !== false && (
        <ClubLeaderboardCard key={`clubs-${showId}`} showId={showId} showName={currentShow.name} />
      )}
    </div>
  )
}
